import React from 'react';
import { truncateWithEllipsis } from '../../utils';
import { useDataContext } from '../../context/data.js';
import translations from '../../constants/translations.json';

const MenuItem = ({
  item,
  isMenuBuyable,
  isDineIn,
  isTakeOut,
  // Can this venue take an order at all? A theater selling only pre-orders has
  // neither immediate flag set and still sells — see utils/orderTypes
  // venueSellsOnline. Left undefined by callers that never had the question,
  // and then the old two-flag answer stands.
  canOrder,
  menuPlaceholder,
  currency,
  defaultImg,
  onItemSelectionModalOpen,
  progress,
}) => {
  // Coalesced: MenuItem is rendered (and unit-tested) outside a DataWrapper in
  // places, and a missing provider must not take the whole menu down — the badge
  // simply falls back to its built-in labels.
  const { language } = useDataContext() || {};

  // Per-product stampcard badge (section 9.2). Shows that the product qualifies for a
  // stampcard ("⭐ Klippekort"), plus live progress when the customer is signed in.
  // Fail-safe: no qualifying campaign for this product -> progress undefined -> render nothing.
  const renderProgressPill = () => {
    const maxCount = Number(progress?.maxCount);
    if (!progress || !Number.isFinite(maxCount) || maxCount <= 0) return null;

    const label = translations[language]?.stamp_card || 'Klippekort';
    const currentCount = Number(progress.currentCount) || 0;
    // A completed-but-unredeemed card -> reward ready.
    const rewardReady = currentCount > 0 && currentCount % maxCount === 0;
    const inProgress = currentCount % maxCount;

    if (rewardReady) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-[#96c11f] text-white px-2 py-0.5 text-[10px] sm:text-xs font-bold">
          <i className="fa fa-star" />
          {label} · {translations[language]?.stamp_reward_ready || 'Reward ready'}
        </span>
      );
    }

    // Explicit hex, not bg-primary/10: `primary` is a CSS variable, so Tailwind
    // cannot apply the alpha modifier and renders the pill solid green under
    // green text. Same reason StampProgressCard uses the palette directly.
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-[#eef6da] text-[#3f6212] px-2 py-0.5 text-[10px] sm:text-xs font-semibold">
        <i className="fa fa-star" />
        {label}
        {currentCount > 0 && (
          <span className="font-bold">
            {inProgress}/{maxCount}
          </span>
        )}
      </span>
    );
  };

  // HeyWaitr+ member pricing: the backend returns a discounted `price` plus the
  // original `oldPrice` and `premiumDiscount` only for subscribers. Guarded so
  // non-members see the unchanged single-price layout.
  const hasMemberPrice =
    item?.oldPrice != null && Number(item.oldPrice) > Number(item.price);

  return (
    <div
      key={item.id}
      className={`menu-item flex items-center bg-white rounded-[10px] sm:rounded-[20px] overflow-hidden hover:border-primary ${
        (!isMenuBuyable || !(canOrder === undefined ? isDineIn || isTakeOut : canOrder)) &&
        'hover:!border-[#fff] !cursor-not-allowed'
      }`}
      onClick={(e) => {
        e.stopPropagation();
        onItemSelectionModalOpen();
      }}
    >
      <div className="px-[12px] lg:!px-8 py-2 lg:!py-6 flex flex-col justify-between w-full h-full">
        <div>
          <h2 className="text-sm sm:text-base font-bold mb-1">{item.name}</h2>
          <p className="text-gray-500 text-xs sm:text-sm whitespace-pre-wrap">
            {truncateWithEllipsis(item.description, 50)}
          </p>
        </div>
        <div className="mt-2 flex items-center gap-2 flex-wrap text-xs sm:text-sm font-bold">
          {hasMemberPrice ? (
            <span className="inline-flex items-baseline gap-2">
              <span className="text-[#96c11f]">
                {currency} {item.price}
              </span>
              <span className="text-gray-400 font-medium line-through">
                {currency} {item.oldPrice}
              </span>
            </span>
          ) : (
            <span>
              {currency} {item.price}
            </span>
          )}
          {renderProgressPill()}
        </div>
      </div>

      {(item.image || menuPlaceholder) && (
        <div className="relative shrink-0">
          {hasMemberPrice && item.premiumDiscount != null && (
            <div className="absolute top-2 left-2 z-10 bg-[#96c11f] text-white rounded-lg px-2 py-1 text-[11px] font-bold leading-none">
              -{item.premiumDiscount}%
            </div>
          )}
          <img
            src={item.image || menuPlaceholder}
            alt={item.name}
            className="min-w-28 w-28 sm:min-w-48 sm:w-48 min-h-24 h-24 sm:min-h-48 sm:h-48 object-cover"
          />
        </div>
      )}
    </div>
  );
};

export default MenuItem;
